import InputTypesEnum from '../../../Enums/InputTypesEnum';
import ImportCoveredChecklistFieldsArray from '../../../Enums/ImportCoveredChecklistFieldsArray';
import ErrorMessage from '../../../Elements/ErrorMessage';
import OpportunitySecurityChecklistService from '../../../Services/opportunitySecurityChecklistService';
import OpportunitySecurityChecklistItem from '../../../Domain/OpportunitySecurityChecklistItem';
import EditOpportunitySecurityChecklistWindowDescription from './EditOpportunitySecurityChecklistWindowDescription';

// Step 2 of Import from Contract: the side-by-side review. It is the ORDINARY checklist edit wizard in its
// side-by-side variant - left the checklist as it stands, right the same form pre-filled from the contract - so
// every field, type, validator and hide rule is inherited. Added here: the confirmation page and the audit save.
// Import from Schedule D/E reviews the same way, through a subclass that builds its pages with the two builders below.
export default class ImportContractReviewWindowDescription extends EditOpportunitySecurityChecklistWindowDescription {
  static titleForCreate = 'Review Security Checklist from Contract';
  static titleForEdit = 'Review Security Checklist from Contract';
  static finishButtonLabel = 'Save reviewed checklist';

  // Restated though inherited: GeneralEditWizzardPageFigma SPREADS the description into its state, and a spread
  // copies own properties only.
  static itemType = OpportunitySecurityChecklistItem;
  static messageOnSuccess = 'The Security Checklist has been saved from the contract import.';

  // Panel headers of the side-by-side wizard.
  static readOnlySideLabel = 'Current checklist';
  static editSideLabel = 'AI extracted contract values';

  // The regular save, flagged as a contract import: that only adds an audit comment naming the file, which a
  // plain save does not write. The file name rides on the item as FileName.
  static serviceFunction = (edited) => OpportunitySecurityChecklistService.UpsertChecklist(edited, true);

  // Adds the not-found caption to each covered field through the info prop - a caption line UNDER the input, so it
  // never blanks or corrupts the number/bool/phone/contact values the way a placeholder or a written-in value would.
  // Dormant for the plain edit and copy wizards, whose items never carry FieldsNotFoundInExtraction; new field
  // objects are built so those shared field definitions stay caption-free.
  static withNotFoundInfo(pages, coveredFields, caption) {
    const covered = new Set(coveredFields);
    return pages.map((page) => ({
      ...page,
      fields: page.fields.map((field) =>
        covered.has(field.fieldName)
          ? {
              ...field,
              info: (item) => (item?.FieldsNotFoundInExtraction?.includes(field.fieldName) ? caption : ''),
            }
          : field
      ),
    }));
  }

  // The last page: the reviewer's confirmation that the values were checked against their source.
  static confirmationPage(confirmationLabel, warning, missingConfirmationMessage) {
    return {
      label: 'Confirmation',
      description: 'Compare the values against the current checklist on the left, then confirm to save.',
      fields: [
        {
          fieldName: 'ImportConfirmed',
          label: confirmationLabel,
          className: 'col-12',
          type: InputTypesEnum.OnOff,
          // Nothing to confirm against on the read-only side - the editable panel only.
          hideOnReadOnlySide: true,
          // Switching it ON moves the responsibility to the user, so the warning belongs there - a popup, not a
          // toast, and only on the way up, so turning it off is silent.
          onFieldChange: (editedItem) => {
            if (editedItem.ImportConfirmed === true) {
              ErrorMessage.SetWarning(warning);
            }
          },
          // The wizard validates every page once on the last one, so Finish stays disabled until this is on.
          validityChecker: (value) => (value === true ? '' : missingConfirmationMessage),
        },
      ],
    };
  }

  static pagesInGeneralEditWizzard = [
    ...ImportContractReviewWindowDescription.withNotFoundInfo(
      EditOpportunitySecurityChecklistWindowDescription.pagesInGeneralEditWizzard,
      ImportCoveredChecklistFieldsArray,
      'Not found during AI extraction'
    ),
    ImportContractReviewWindowDescription.confirmationPage(
      'I have double-checked the extracted data against the contract and confirm its accuracy.',
      'Automated contract extraction — values are unvalidated. By saving, you confirm you have checked them against the contract and take responsibility for their accuracy.',
      'You must confirm the values match the contract before saving.'
    ),
  ];
}
